/**
 * Minimal GraphQL transport for the Support Chat POC.
 *
 * Same plumbing as src/app/relay/environment.ts (ant endpoint + Bearer token
 * from the stored admin), without Relay: the console polls plain queries every
 * few seconds and needs nothing from the Relay store.
 */

import { getCredentials } from '../../api/http';
import { getGraphQLEndpoint } from '../../api/graphql/endpoints';

export interface MutationError {
  message: string | null;
  code: string | null;
  fields: string[] | null;
}

/** Every ant mutation returns this envelope. */
export interface MutationPayload<T> {
  result: T | null;
  errors: MutationError[];
}

interface GraphQLResponseBody<TData> {
  data?: TData | null;
  errors?: Array<{ message?: string }>;
}

export async function postGraphQL<TData>(
  query: string,
  variables: Record<string, unknown> = {}
): Promise<TData> {
  const { graphqlToken } = getCredentials();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (graphqlToken) {
    headers.Authorization = `Bearer ${graphqlToken}`;
  }

  const response = await fetch(getGraphQLEndpoint(), {
    method: 'POST',
    headers,
    body: JSON.stringify({ query, variables }),
  });

  if (response.status === 401) {
    window.dispatchEvent(new CustomEvent('api:unauthorized'));
  }

  let json: GraphQLResponseBody<TData> | null = null;
  try {
    json = (await response.json()) as GraphQLResponseBody<TData>;
  } catch (e) {
    json = null;
  }

  if (!response.ok) {
    throw new Error(
      json?.errors?.[0]?.message ||
        `GraphQL request failed with status ${response.status}`
    );
  }
  if (json?.errors?.length) {
    throw new Error(
      json.errors.map((e) => e.message || 'Unknown GraphQL error').join('; ')
    );
  }
  if (!json?.data) {
    throw new Error('GraphQL request returned no data');
  }
  return json.data;
}

/**
 * ant refused for lack of permission. AshGraphql answers a policy refusal
 * with the one word "forbidden" (message and code alike), which
 * unwrapMutation throws as is, one per "; " when there are several. A
 * "forbidden field" is another thing: a field the token may not read on a
 * record it may.
 */
export function isForbiddenError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return message
    .split(';')
    .map((part) => part.trim().replace(/\s*\(.*\)$/, ''))
    .some((part) => part.toLowerCase() === 'forbidden');
}

export function formatMutationErrors(errors: MutationError[]): string {
  return errors
    .map((e) => {
      const fields = e.fields?.length ? ` (${e.fields.join(', ')})` : '';
      return `${e.message || e.code || 'Unknown error'}${fields}`;
    })
    .join('; ');
}

/**
 * Turns a mutation envelope into its result, throwing the `errors` array as
 * one Error so the pages can show it.
 */
export function unwrapMutation<T>(
  payload: MutationPayload<T> | null | undefined,
  operation: string
): T {
  if (!payload) {
    throw new Error(`${operation}: empty response`);
  }
  if (payload.errors?.length) {
    throw new Error(formatMutationErrors(payload.errors));
  }
  if (payload.result === null || payload.result === undefined) {
    throw new Error(`${operation}: no result`);
  }
  return payload.result;
}
